import { pxToRem } from 'utils/convert';
import { ORG_PERMISSIONS, PROJECT_PERMISSIONS } from 'utils/permissions';
import { Alignment, Categories, PermissionsMapKey } from './types';

const {
  DraftMessaging,
  EmailChannel,
  EmbeddedChannel,
  PushInAppChannel,
  ManageBaseTemplates,
  ManageCatalogs,
  ManageChannelConfiguration,
  ManageGoals,
  ManageIntegrations,
  ManageMessaging,
  ManageProjectSettings,
  ManageReports,
  ManageSegmentation,
  ManageSnippets,
  ManageUserProfiles,
  PushChannel,
  SMSChannel,
  ViewAndExportUserData,
  ViewCatalogs,
  ViewChannelConfiguration,
  ViewGoals,
  ViewMessaging,
  ViewReports,
  PushWebChannel,
  WhatsAppChannel,
  // additional group permission created here
  CampaignMediumsGroup,
} = PROJECT_PERMISSIONS;

export const TABLE_COLUMNS = [
  {
    id: 'name',
    isSortable: true,
    label: 'Name',
  },
  {
    alignment: Alignment.FLEX_END,
    id: 'permissions',
    isSortable: true,
    label: 'Permissions',
  },
  {
    id: 'members',
    isSortable: true,
    label: 'Members',
  },
  {
    id: 'updatedAt',
    isSortable: true,
    label: 'Last modified',
  },
];

const nameWidth = `5fr`;
const permissionsWidth = `minmax(${pxToRem(120)}, 1fr)`;
const membersWidth = `minmax(${pxToRem(180)}, 1fr)`;
const updatedAtWidth = `minmax(${pxToRem(200)}, 1fr)`;
export const gridLayout = `${nameWidth} ${permissionsWidth} ${membersWidth} ${updatedAtWidth}`;

export const CREATE_ROLE_TEXT = {
  buttonLabel: 'Create role',
  modalId: 'create-role-modal',
  rolesOptionsPlaceholder: 'Select a role (optional)',
  title: 'New role',
};

export const ROLE_NAME = 'roleName';
export const INITIAL_PERMISSIONS = 'initialPermissions';

export const CREATE_ROLE_FORM_LABELS = {
  [ROLE_NAME]: 'Name',
  [INITIAL_PERMISSIONS]: 'Copy permissions from...',
};

/**
 * Permissions
 * meant to be merged with permissions response
 */
export const CATEGORIES: Categories = {
  generalConfiguration: {
    id: 'generalConfiguration',
    name: 'General configuration',
    permissions: [ManageIntegrations, ManageProjectSettings],
  },
  channels: {
    id: 'channels',
    name: 'Channels',
    permissions: [ViewChannelConfiguration, ManageChannelConfiguration],
  },
  journeys: {
    id: 'journeys',
    name: 'Journeys, campaigns & experiments',
    permissions: [ViewMessaging, DraftMessaging, ManageMessaging],
  },
  /**
   * How to add/split Campaigns from Journeys
   * 1. uncomment group below
   * 2. Update name for display purposes
   * 3. Update permissions array to coincide with numbers for permissions in that group
   * 4. Follow pattern from Journeys to add group checkbox control (ie. id: 100 with `children`)
   * 5. Add category to Messaging list in GROUPS
   */
  // campaignsAndExpirements: {
  //   id: 'campaignsAndExpirements',
  //   name: 'Campaign & expirements',
  //   permissions: [SMSChannel, EmailChannel, PushChannel, PushInAppChannel, PushWebChannel],
  // },
  templates: {
    id: 'templates',
    name: 'Templates',
    permissions: [ManageBaseTemplates],
  },
  snippets: {
    id: 'snippets',
    name: 'Snippets',
    permissions: [ManageSnippets],
  },
  catalogs: {
    id: 'catalogs',
    name: 'Catalogs',
    permissions: [ViewCatalogs, ManageCatalogs],
  },
  segmentationAndLists: {
    id: 'segmentationAndLists',
    name: 'Segmentation and lists',
    permissions: [ManageSegmentation],
  },
  userProfilesAndData: {
    id: 'userProfilesAndData',
    name: 'User profiles and data',
    permissions: [ViewAndExportUserData, ManageUserProfiles],
  },
  predictiveGoals: {
    id: 'predictiveGoals',
    name: 'Predictive goals',
    permissions: [ViewGoals, ManageGoals],
  },
  reports: {
    id: 'reports',
    name: 'Reports',
    permissions: [ViewReports, ManageReports],
  },
};

export const GROUPS = {
  project: {
    id: 'project',
    name: 'Project',
    categories: [CATEGORIES.generalConfiguration.id, CATEGORIES.channels.id],
  },
  messaging: {
    id: 'messaging',
    name: 'Messaging',
    categories: [CATEGORIES.journeys.id],
    // categories: [CATEGORIES.journeys.id, CATEGORIES.campaignsAndExpirements.id],
  },
  content: {
    id: 'content',
    name: 'Content',
    categories: [CATEGORIES.templates.id, CATEGORIES.snippets.id, CATEGORIES.catalogs.id],
  },
  audience: {
    id: 'audience',
    name: 'Audience',
    categories: [CATEGORIES.segmentationAndLists.id, CATEGORIES.userProfilesAndData.id],
  },
  insights: {
    id: 'insights',
    name: 'Insights',
    categories: [CATEGORIES.predictiveGoals.id, CATEGORIES.reports.id],
  },
};

export const CAMPAIGN_MEDIUMS = [
  EmailChannel,
  SMSChannel,
  PushInAppChannel,
  PushChannel,
  PushWebChannel,
];

/** returns a map of permissions to object configs for rendering based on enabled FFs */
export const createPermissionsMap: (
  isEmbeddedMessagingEnabled?: boolean,
  isWhatsAppMessagingEnabled?: boolean,
  isAssetManagerIndexFFEnabled?: boolean,
) => PermissionsMapKey = (
  isEmbeddedMessagingEnabled = false,
  isWhatsAppMessagingEnabled = false,
  isAssetManagerIndexFFEnabled = false,
) => ({
  [ManageBaseTemplates]: {
    description: isAssetManagerIndexFFEnabled
      ? 'Create, update and delete templates and creative assets'
      : 'Create, update and delete templates',
    name: 'Create and manage',
  },
  [ManageCatalogs]: {
    description: 'Create, update and delete catalog items',
    name: 'Create and manage',
  },
  [DraftMessaging]: {
    description: 'Create, draft, and copy journeys, campaigns, and experiments',
    name: 'Draft',
  },
  [ManageMessaging]: {
    description: 'Manage and publish journeys',
    name: 'Manage and publish journeys',
  },
  [ManageUserProfiles]: {
    description: 'Update and delete contacts',
    name: 'Create and manage Contacts',
  },
  [ViewAndExportUserData]: {
    description: 'View user profiles',
    name: 'View',
  },
  [ORG_PERMISSIONS.ManageBilling]: {
    description: '',
    name: 'Manage billing',
  },
  [ManageReports]: {
    description: 'Create, update, and delete reports',
    name: 'Create and manage',
  },
  [ViewMessaging]: {
    description: 'View journeys, campaigns, and experiments',
    name: 'View',
  },
  [ORG_PERMISSIONS.CreateProjects]: {
    description: '',
    name: 'Create projects',
  },
  [ManageIntegrations]: {
    description: 'Configure data feeds, third-party integrations, API keys and webhooks',
    name: 'Manage integrations',
  },
  [ViewReports]: {
    description: 'View reports',
    name: 'View',
  },
  [ViewCatalogs]: {
    description: 'View catalogs',
    name: 'View',
  },
  [ManageProjectSettings]: {
    description: 'Modify labels, custom events, etc',
    name: 'Manage settings',
  },
  [ORG_PERMISSIONS.ManageMembers]: {
    description: '',
    name: 'Manage members',
  },
  [ManageSegmentation]: {
    description: 'Create, update, and delete lists',
    name: 'Setup and manage',
  },
  [ManageSnippets]: {
    description: 'Create, update, and delete snippets',
    name: 'Create and manage',
  },
  [ViewChannelConfiguration]: {
    description: 'View channel configuration pages',
    name: 'View',
  },
  [ORG_PERMISSIONS.ManageRoles]: {
    description: '',
    name: 'Manage roles',
  },
  [ManageChannelConfiguration]: {
    description: 'Configure channels',
    name: 'Setup and manage',
  },
  [ManageGoals]: {
    description: 'Create, update, and delete predictive goals',
    name: 'Create and manage',
  },
  [ViewGoals]: {
    description: 'View predictive goals',
    name: 'View',
  },
  // additions here
  [EmailChannel]: {
    description: '',
    name: 'Email',
  },
  [PushInAppChannel]: {
    description: '',
    name: 'In App',
  },
  [PushChannel]: {
    description: '',
    name: 'Push',
  },
  [SMSChannel]: {
    description: '',
    name: 'SMS',
  },
  [PushWebChannel]: {
    description: '',
    name: 'Web Push',
  },
  // may need to add additional details here
  [CampaignMediumsGroup]: {
    children: [
      EmailChannel,
      SMSChannel,
      PushInAppChannel,
      PushChannel,
      PushWebChannel,
      ...(isEmbeddedMessagingEnabled ? [EmbeddedChannel] : []),
      ...(isWhatsAppMessagingEnabled ? [WhatsAppChannel] : []),
    ],
    description: 'Send, schedule, and activate campaigns',
    name: 'Manage and launch campaigns',
  },
  ...(isEmbeddedMessagingEnabled
    ? {
        [EmbeddedChannel]: {
          description: '',
          name: 'Embedded',
        },
      }
    : {}),
  ...(isWhatsAppMessagingEnabled
    ? {
        [WhatsAppChannel]: {
          description: '',
          name: 'WhatsApp',
        },
      }
    : {}),
});

/**
 * Create group object to use as toggle for sub-list of permissions by message medium
 */
export const messageMediumGroup = {
  id: 100,
  scope: 'Project',
  category: 'Teams',
  name: 'Manage and launch',
  requires: [],
};
